import { useState } from "react";
import { t } from "@/lib/i18n";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, Clock, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import PageHeader from "@/components/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = [
  { key: "pickup_help", label: "Help with a pickup" }, { key: "kabadiwala", label: "I'm a kabadiwala" },
  { key: "city_request", label: "Bring Kindsaver to my city" }, { key: "business", label: "Business / society tie-up" },
  { key: "feedback", label: "Feedback" }, { key: "other", label: "Something else" },
];
const EMPTY = { name: "", phone: "", email: "", city: "", locality: "", message: "", website: "" };
const inputCls = "w-full px-4 py-3 rounded-xl border border-[rgba(20,40,26,.12)] bg-[var(--paper)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--leaf)]/30 focus:bg-white transition-colors";

export default function Contact() {
  const { content: c } = useSite();
  const [params] = useSearchParams();
  const [type, setType] = useState(TYPES.some((tp) => tp.key === params.get("type")) ? params.get("type") : "pickup_help");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries", { ...form, type });
      setDone(data.reference);
      setForm(EMPTY);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const CARDS = [
    { icon: Phone, label: "Call", value: c.phone, href: `tel:${c.phone.replace(/\s/g, "")}`, testid: "contact-call" },
    { icon: MessageCircle, label: "WhatsApp", value: "Chat with us", href: `https://wa.me/${c.whatsapp}`, testid: "contact-whatsapp" },
    { icon: Mail, label: "Email", value: c.email, href: `mailto:${c.email}`, testid: "contact-email" },
  ];

  return (
    <div data-testid="contact-page">
      <PageHeader testid="contact-header" eyebrow="Contact" title={t("Talk to a real person.")}
        subtitle="Questions about a pickup, joining as a kabadiwala, or bringing Kindsaver to your city — we usually reply within a working day." />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-3">
          {CARDS.map(({ icon: Icon, label, value, href, testid }) => (
            <a key={t(label)} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testid}
              className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-[rgba(20,40,26,.08)] card-lift">
              <span className="w-12 h-12 rounded-2xl bg-[var(--leaf)] text-white flex items-center justify-center"><Icon size={20} /></span>
              <div><div className="text-xs font-bold uppercase tracking-wider text-[var(--ink-soft)]">{t(label)}</div><div className="font-semibold">{value}</div></div>
            </a>
          ))}
          <div className="p-5 rounded-2xl bg-[var(--stone)] space-y-3 text-sm" data-testid="contact-address">
            <div className="flex gap-3"><MapPin size={18} className="shrink-0 text-[var(--leaf-dark)]" />{c.address}</div>
            <div className="flex gap-3"><Clock size={18} className="shrink-0 text-[var(--leaf-dark)]" />{c.hours}</div>
          </div>
        </div>

        <div className="lg:col-span-7 bg-white rounded-3xl border border-[rgba(20,40,26,.08)] p-6 sm:p-8">
          {done ? (
            <div className="text-center py-10 fade-up" data-testid="contact-success">
              <CheckCircle2 size={48} className="mx-auto text-[var(--leaf)]" />
              <h2 className="mt-4 text-2xl font-bold">{t("Thank you! We've got your message.")}</h2>
              <p className="mt-2 text-[var(--ink-soft)]">{t("Your reference is")} <b data-testid="contact-reference">{done}</b>{t(". We'll get back to you soon.")}</p>
              <button onClick={() => setDone(null)} className="btn-ghost mt-6 text-sm" data-testid="contact-send-another">{t("Send another message")}</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <h2 className="text-xl sm:text-2xl font-bold">{t("Send us a message")}</h2>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-12 rounded-xl bg-[var(--paper)]" data-testid="contact-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((tp) => <SelectItem key={tp.key} value={tp.key} data-testid={`contact-type-${tp.key}`}>{t(tp.label)}</SelectItem>)}</SelectContent>
              </Select>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required minLength={2} value={form.name} onChange={set("name")} placeholder={t("Your name *")} className={inputCls} data-testid="contact-name-input" />
                <input value={form.phone} onChange={set("phone")} placeholder={t("Phone number")} inputMode="tel" className={inputCls} data-testid="contact-phone-input" />
                <input type="email" value={form.email} onChange={set("email")} placeholder={t("Email (optional)")} className={inputCls} data-testid="contact-email-input" />
                <input value={form.city} onChange={set("city")} placeholder={type === "city_request" ? t("Your city *") : t("City")} required={type === "city_request"} className={inputCls} data-testid="contact-city-input" />
              </div>
              <input value={form.locality} onChange={set("locality")} placeholder={t("Area / locality (optional)")} className={inputCls} data-testid="contact-locality-input" />
              <textarea value={form.message} onChange={set("message")} rows={4} placeholder={t("How can we help?")} className={inputCls} data-testid="contact-message-input" />
              <input value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <p className="text-xs text-[var(--ink-soft)]">{t("Share a phone number or email so we can reach you.")}</p>
              <button disabled={busy} className="btn-leaf w-full sm:w-auto" data-testid="contact-submit-btn">{busy && <Loader2 size={16} className="animate-spin" />} Send message</button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
